import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  InputAdornment,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit, ArrowBack, Search } from '@mui/icons-material';

const NAVY = '#0D1B35';
const BLUE = '#0066CC';
import apiClient from '../lib/axios';

interface PharmaceuticalForm {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

const PAGE_SIZE = 50;

export const PharmaceuticalForms: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selected, setSelected] = useState<PharmaceuticalForm | null>(null);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data: result, isLoading } = useQuery({
    queryKey: ['pharmaceutical-forms', debouncedSearch, page],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: PharmaceuticalForm[]; total: number }>('/products/pharmaceutical-forms', {
        params: { query: debouncedSearch || undefined, limit: PAGE_SIZE, offset: page * PAGE_SIZE },
      });
      return data;
    },
  });

  const forms = result?.data ?? [];
  const total = result?.total ?? 0;

  const createMutation = useMutation({
    mutationFn: async (name: string) => {
      await apiClient.post('/products/pharmaceutical-forms', { name });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmaceutical-forms'] });
      setCreateDialogOpen(false);
      setName('');
      setError(null);
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || t('catalog.errors.createPharmaceuticalForm'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      await apiClient.put(`/products/pharmaceutical-forms/${id}`, { name });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmaceutical-forms'] });
      setEditDialogOpen(false);
      setSelected(null);
      setName('');
      setError(null);
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || t('catalog.errors.updatePharmaceuticalForm'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/products/pharmaceutical-forms/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmaceutical-forms'] });
      setDeleteDialogOpen(false);
      setSelected(null);
      setError(null);
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || t('catalog.errors.deletePharmaceuticalForm'));
    },
  });

  const handleCreateClick = () => {
    setName('');
    setError(null);
    setCreateDialogOpen(true);
  };

  const handleEditClick = (form: PharmaceuticalForm) => {
    setSelected(form);
    setName(form.name);
    setError(null);
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (form: PharmaceuticalForm) => {
    setSelected(form);
    setError(null);
    setDeleteDialogOpen(true);
  };

  return (
    <Box>
      {/* Hero */}
      <Box
        sx={{
          borderRadius: 4,
          bgcolor: NAVY,
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)',
          backgroundSize: '20px 20px',
          p: { xs: '28px 24px', sm: '40px 44px' },
          mb: 3,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ position: 'absolute', width: 260, height: 260, borderRadius: '50%', bgcolor: 'rgba(0,102,204,0.12)', top: -80, right: -60, pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', width: 140, height: 140, borderRadius: '50%', bgcolor: 'rgba(0,102,204,0.08)', bottom: -50, left: '35%', pointerEvents: 'none' }} />
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
              <IconButton size="small" onClick={() => navigate('/admin/dashboard')} sx={{ color: 'rgba(255,255,255,0.5)', p: 0.5 }}>
                <ArrowBack fontSize="small" />
              </IconButton>
              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase' }}>
                FORMULACIONES
              </Typography>
            </Box>
            <Typography variant="h2" sx={{ color: '#fff', mb: 0.5 }}>
              {t('catalog.pharmaceuticalForms.title')}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: { xs: 13, sm: 14 } }}>
              Formas farmacéuticas del catálogo de productos
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateClick}
            sx={{ bgcolor: BLUE, flexShrink: 0, mt: { xs: 0, sm: 1 }, '&:hover': { bgcolor: '#0052a3' } }}
          >
            {t('catalog.pharmaceuticalForms.add')}
          </Button>
        </Box>
      </Box>

      <Box display="flex" justifyContent="flex-end" mb={2}>
        <TextField
          size="small"
          placeholder={t('catalog.search')}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ width: 220 }}
        />
      </Box>

      <Paper>
        {isLoading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('catalog.pharmaceuticalForms.nameLabel')}</TableCell>
                  <TableCell>{t('catalog.table.created')}</TableCell>
                  <TableCell align="right">{t('catalog.table.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {forms.length > 0 ? (
                  forms.map((form) => (
                    <TableRow key={form.id}>
                      <TableCell>{form.name}</TableCell>
                      <TableCell>{new Date(form.createdAt).toLocaleDateString()}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" color="primary" onClick={() => handleEditClick(form)}>
                          <Edit />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteClick(form)}>
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Typography color="text.secondary" py={4}>
                        {debouncedSearch ? t('catalog.noResults') : t('catalog.pharmaceuticalForms.empty')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
        />
      </Paper>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('catalog.pharmaceuticalForms.createTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('catalog.pharmaceuticalForms.createDescription')}
          </DialogContentText>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            autoFocus
            label={t('catalog.pharmaceuticalForms.nameLabel')}
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={name.length > 0 && name.length < 2}
            helperText={name.length > 0 && name.length < 2 ? t('catalog.pharmaceuticalForms.nameMinLength') : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>{t('catalog.buttons.cancel')}</Button>
          <Button
            onClick={() => createMutation.mutate(name.trim())}
            variant="contained"
            disabled={name.trim().length < 2 || createMutation.isPending}
          >
            {createMutation.isPending ? t('catalog.buttons.creating') : t('catalog.buttons.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('catalog.pharmaceuticalForms.editTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('catalog.pharmaceuticalForms.editDescription')}
          </DialogContentText>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            autoFocus
            label={t('catalog.pharmaceuticalForms.nameLabel')}
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={name.length > 0 && name.length < 2}
            helperText={name.length > 0 && name.length < 2 ? t('catalog.pharmaceuticalForms.nameMinLength') : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>{t('catalog.buttons.cancel')}</Button>
          <Button
            onClick={() => selected && updateMutation.mutate({ id: selected.id, name: name.trim() })}
            variant="contained"
            disabled={name.trim().length < 2 || updateMutation.isPending}
          >
            {updateMutation.isPending ? t('catalog.buttons.updating') : t('catalog.buttons.update')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('catalog.pharmaceuticalForms.deleteTitle')}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <DialogContentText>
            {t('catalog.pharmaceuticalForms.deleteConfirm')} <strong>{selected?.name}</strong>?
            {!error && ` ${t('catalog.pharmaceuticalForms.deleteCannotUndo')}`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('catalog.buttons.cancel')}</Button>
          <Button
            onClick={() => selected && deleteMutation.mutate(selected.id)}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? t('catalog.buttons.deleting') : t('catalog.buttons.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
